/**
 * Creating, joining and leaving a party.
 *
 * # The one thing not to break
 *
 * **A display must never need an account.** This file used to say the opposite
 * about everybody — ADR 0007's ten-second join, where a stranger got a seat from
 * a code and a name — and ADR 0027 (phase 20f-1) reversed it for *players*: a
 * private installation whose party door stands open is a private login page in
 * front of a public game. What survives untouched is the screen. `/display`
 * calls `joinParty` with `as_display` and no session, because a television has
 * no keyboard worth using (ADR 0005) and renders rather than plays, and the
 * literal reading of the new rule would turn every one of them off.
 *
 * The seat is held by an `HttpOnly` device cookie the browser attaches on its
 * own; nothing here reads or stores a token. That is also what makes `rejoin`
 * work after a refresh with no state kept anywhere in the app.
 */

import {
  apiGet,
  apiPost,
  HttpError,
  type ConnectedView,
  type CreatePartyRequest,
  type JoinPartyRequest,
  type PreviewView,
  type SeatView,
  type TicketView,
} from "./http.js";

/** Create a party. Needs a signed-in account: the host's account owns it. */
export async function createParty(request: CreatePartyRequest = {}): Promise<SeatView> {
  return apiPost<SeatView>("/party", request);
}

/**
 * Join a party.
 *
 * If the browser is already holding a device cookie for *this* party, the server
 * returns the existing seat rather than a second one — so this doubles as the
 * reconnect path and the caller does not have to know which case it is in.
 * ⚠️ The rejoin is gated too: a seat outlives a lost signal, not a rejection.
 *
 * Rejects with a 401 for a player holding no session (the caller sends them to
 * `/register?code=…`) and a 403 for one whose account is `pending` or
 * `rejected`.
 */
export async function joinParty(request: JoinPartyRequest): Promise<SeatView> {
  return apiPost<SeatView>("/party/join", request);
}

/**
 * Look a code up before committing to it.
 *
 * Resolves to `null` when no party is open with that code — that is the normal
 * answer for a mistyped code, not an error worth showing a stack of.
 */
export async function previewParty(code: string): Promise<PreviewView | null> {
  try {
    return await apiGet<PreviewView>(`/party/${encodeURIComponent(code)}`);
  } catch (error) {
    if (error instanceof HttpError && error.status === 400) {
      return null;
    }
    throw error;
  }
}

/**
 * Exchange the device cookie for a **socket ticket**.
 *
 * The seat is `HttpOnly`, so script cannot read it and cannot present it to the
 * realtime plane. This asks the server — which *can* read it, because the
 * browser attaches the cookie itself — for a one-shot credential that stands in
 * for it for sixty seconds.
 *
 * Ask for a fresh one on every connect, including a reconnect: they are single
 * use by design, so holding one and retrying with it is the version that stops
 * working the second time.
 *
 * Rejects with an `HttpError` of 401 when the browser is not holding a seat for
 * any party — the caller's cue to send somebody to the join screen rather than
 * to show an error.
 */
export async function socketTicket(seatToken?: string): Promise<TicketView> {
  // A browser sends nothing: the `HttpOnly` device cookie rides along on its
  // own and the server reads it. `seatToken` is the packaged television's path
  // (phase 11c) — it holds its device token in the app's own storage, because
  // a `file://` page is cross-origin to the server and a `SameSite=Lax` cookie
  // is not sent cross-site whatever CORS allows.
  return apiPost<TicketView>("/party/ticket", {}, seatToken);
}

/**
 * Claim a television's pairing code and attach it to this party (phase 11c).
 *
 * **Host only, and the party is not a parameter**: it comes from the caller's
 * own device cookie, so a code cannot be redeemed into a room the caller is not
 * in. Same discipline as every game intent.
 */
export async function connectDisplay(code: string): Promise<ConnectedView> {
  return apiPost<ConnectedView>("/party/display", { code });
}

/** Give up the seat and clear the device cookie. Safe to retry. */
export async function leaveParty(): Promise<void> {
  await apiPost<{ ok: boolean }>("/party/leave", {});
}
